import type { AuditScoreType } from "@ots-frontend/ots-data";
import { DescriptionItem, DescriptionList } from "../components/DescriptionList";
import { OtsBadge } from "../components/OtsBadge";
import { auditScoreLabels, formatConfidence } from "../audit/AuditScoreRing";
import {
  formatActivityDuration,
  formatActivityMoney,
  formatActivityQuantity,
  formatActivityTime,
} from "./driver-activity-format";
import { activityKindTheme } from "./driver-activity-theme";
import { type ActivityEntry, secondScadaQty } from "./driver-activity-view-model";

export type ActivityEntryPreviewProps = {
  entry: ActivityEntry;
  timeZone?: string;
  showRevenue?: boolean;
  auditScoreType?: AuditScoreType;
};

/** A ticket at a glance, for a hover card: enough to decide whether it is worth opening. */
export const ActivityEntryPreview = ({ entry, timeZone, showRevenue = false, auditScoreType = "BUSINESS_VALIDATION_CONFIDENCE" }: ActivityEntryPreviewProps) => {
  const { pickup, delivery, tta, audit } = entry;
  const theme = activityKindTheme[entry.kind];
  const validated = entry.validation.filter((step) => step.validatedDttm != null).map((step) => step.label);
  const scada = pickup && [pickup.scadaQty, secondScadaQty(pickup)].filter((qty) => qty != null);
  const muted = "text-[var(--ots-text-muted)]";

  const stop = (side: string, transport: typeof pickup, time?: number) => transport && (
    <div className="flex items-baseline justify-between gap-3">
      <span className="min-w-0 truncate"><span className={muted}>{side} </span><strong>{transport.siteName ?? "Unknown site"}</strong> · {transport.containerName}</span>
      <span className="shrink-0 tabular-nums">{formatActivityTime(time, timeZone)} · {formatActivityQuantity(transport.quantityDecimal, transport.quantityUnitOfMeasure)}</span>
    </div>
  );

  return (
    <div className="w-80 space-y-2 text-xs leading-5">
      <div className="flex items-center justify-between gap-2">
        <strong className="truncate font-mono text-sm">{entry.resourceId ?? "No ID"}</strong>
        <OtsBadge tone={entry.isServiceTicket ? "yellow" : theme.badgeTone} size="compact">{entry.isServiceTicket ? "Service work" : theme.label}</OtsBadge>
      </div>
      <div>
        {stop("From", pickup, tta?.pickupDttm ?? pickup?.createDttm)}
        {stop("To", delivery, tta?.deliveryDttm ?? delivery?.createDttm)}
      </div>
      <DescriptionList layout="inline" compact>
        <DescriptionItem label="Freight">{[pickup?.waterTypeName ?? delivery?.waterTypeName, pickup?.serviceTypeName ?? delivery?.serviceTypeName].filter(Boolean).join(" · ")}</DescriptionItem>
        {!!scada?.length && <DescriptionItem label="SCADA">{scada.map((qty) => formatActivityQuantity(qty)).join(" / ")} {pickup?.quantityUnitOfMeasure}</DescriptionItem>}
        {entry.durationMillis != null && <DescriptionItem label="Turnaround">{formatActivityDuration(entry.durationMillis)}</DescriptionItem>}
        {showRevenue && entry.revenue != null && <DescriptionItem label="Revenue">{formatActivityMoney(entry.revenue)}</DescriptionItem>}
        {entry.validation.length > 0 && <DescriptionItem label="Validated">{validated.join(", ") || "not yet"}</DescriptionItem>}
        {audit?.hasAuditRun && (
          <DescriptionItem label="Audit">
            {auditScoreLabels[auditScoreType]} {formatConfidence(audit.confidences[auditScoreType])}
            {audit.blockingFindingCount > 0 && <strong className="text-[var(--ots-error)]"> · {audit.blockingFindingCount} blocking</strong>}
          </DescriptionItem>
        )}
      </DescriptionList>
      {entry.warnings.length > 0 && (
        <ul className="space-y-0.5 rounded bg-[var(--ots-danger-container)] px-2 py-1 text-[var(--ots-on-danger-container)]">
          {entry.warnings.slice(0, 3).map((warning) => <li key={warning.type}>{warning.message}</li>)}
        </ul>
      )}
      {entry.messages.length > 0 && <p className={["truncate italic", muted].join(" ")}>“{entry.messages[entry.messages.length - 1].message}”</p>}
      {entry.pictures.length > 0 && (
        <div className="flex gap-1">
          {entry.pictures.slice(0, 4).map((picture) => <img key={picture.id} src={picture.url} alt="" className="size-16 rounded border border-[var(--ots-outline-variant)] object-cover"/>)}
        </div>
      )}
      <p className={["text-[10px]", muted].join(" ")}>Click the row for everything else.</p>
    </div>
  );
};
